/**
 * Branded sign-in: every refusal code the API returns (apps/api/src/auth/sign-in/sign-in-error-codes.ts) -> the full
 * translation key the screen shows (3 Nov 2026; contract docs/superpowers/specs/2026-11-03-branded-sign-in-design.md
 * section 5). A refusal is worded from the reader's catalogue, never from the API's English message (CLAUDE.md rule 9).
 *
 *   signInErrors.*            messages/sign-in-errors (builder 9 writes EVERY key below, in en, ar, hi and fr)
 *   signInApps.testFailure.*  messages/sign-in-apps (builder 12: C6, each with .title and .fix)
 *
 * Frozen after wave 0 (coordinator). Builder 9 owns lib/sign-in/__tests__/error-keys-parity.spec.ts: every API code
 * has a key here, and every key exists in all four catalogues.
 */

/** HTTP refusal code -> key. */
export const SIGN_IN_ERROR_KEYS: Readonly<Record<string, string>> = {
  // Signing in
  sign_in_method_off: 'signInErrors.methodOff',
  sign_in_wrong_company_address: 'signInErrors.wrongCompanyAddress',
  sign_in_connection_unavailable: 'signInErrors.connectionUnavailable',
  sign_in_tenant_mismatch: 'signInErrors.tenantMismatch',
  sign_in_guest_refused: 'signInErrors.guestRefused',
  sign_in_domain_not_allowed: 'signInErrors.domainNotAllowed',
  sign_in_email_unverified: 'signInErrors.emailUnverified',
  sign_in_no_account: 'signInErrors.noAccount',
  sign_in_provider_refused: 'signInErrors.providerRefused',
  sign_in_handoff_expired: 'signInErrors.handoffExpired',
  sign_in_return_origin_refused: 'signInErrors.returnOriginRefused',
  sign_in_binding_required: 'signInErrors.bindingRequired',
  sign_in_emergency_two_factor_missing: 'signInErrors.emergencyTwoFactorMissing',
  // Settings > Security > Sign-in
  sign_in_policy_forbidden: 'signInErrors.policyForbidden',
  sign_in_company_owner_only: 'signInErrors.companyOwnerOnly',
  sign_in_last_method: 'signInErrors.lastMethod',
  sign_in_lockout_checks: 'signInErrors.lockoutChecks',
  sign_in_emergency_owner_invalid: 'signInErrors.emergencyOwnerInvalid',
  sign_in_method_unusable: 'signInErrors.methodUnusable',
  sign_in_workspace_invalid: 'signInErrors.workspaceInvalid',
  // Sign-in apps
  sign_in_connection_exists: 'signInErrors.connectionExists',
  sign_in_connection_invalid: 'signInErrors.connectionInvalid',
  sign_in_connection_untested: 'signInErrors.connectionUntested',
  sign_in_connection_not_found: 'signInErrors.connectionNotFound',
  sign_in_connection_in_use: 'signInErrors.connectionInUse',
  sign_in_test_not_found: 'signInErrors.testNotFound',
  // Company addresses
  company_address_off: 'signInErrors.addressOff',
  company_address_label_invalid: 'signInErrors.addressLabelInvalid',
  company_address_label_reserved: 'signInErrors.addressLabelReserved',
  company_address_taken: 'signInErrors.addressTaken',
  company_address_exists: 'signInErrors.addressExists',
  company_address_not_found: 'signInErrors.addressNotFound',
  company_address_publish_failed: 'signInErrors.addressPublishFailed',
  company_address_use_invalid: 'signInErrors.addressUseInvalid',
  company_address_wrong_company: 'signInErrors.addressWrongCompany',
  // Review finding F4 (4 Nov 2026): the admin use asked of an own domain; points to the company address card.
  company_address_console_own_domain: 'signInErrors.addressConsoleOwnDomain',
  // Super admin
  sign_in_reset_reason_required: 'signInErrors.resetReasonRequired',
}

/** Any code not above (an older API, a network failure). */
export const SIGN_IN_ERROR_UNKNOWN_KEY = 'signInErrors.unknown'

/** The key for a refusal code; never undefined. */
export function signInErrorKey(code: string | null | undefined): string {
  return (code && SIGN_IN_ERROR_KEYS[code.trim()]) || SIGN_IN_ERROR_UNKNOWN_KEY
}

/** Test sign-in outcome (sign_in_connections.last_test_detail.errorCode) -> key stem; add `.title` / `.fix`. */
export const SIGN_IN_TEST_FAILURE_KEYS: Readonly<Record<string, string>> = {
  sign_in_test_redirect_mismatch: 'signInApps.testFailure.redirectMismatch',
  sign_in_test_bad_secret: 'signInApps.testFailure.badSecret',
  sign_in_test_secret_expired: 'signInApps.testFailure.secretExpired',
  sign_in_test_app_not_found: 'signInApps.testFailure.appNotFound',
  sign_in_test_consent_required: 'signInApps.testFailure.consentRequired',
  sign_in_test_user_not_assigned: 'signInApps.testFailure.userNotAssigned',
  sign_in_test_tenant_mismatch: 'signInApps.testFailure.tenantMismatch',
  sign_in_test_email_mismatch: 'signInApps.testFailure.emailMismatch',
  sign_in_test_domain_not_allowed: 'signInApps.testFailure.domainNotAllowed',
  sign_in_test_email_unverified: 'signInApps.testFailure.emailUnverified',
  sign_in_test_guest_refused: 'signInApps.testFailure.guestRefused',
  sign_in_test_cancelled: 'signInApps.testFailure.cancelled',
  sign_in_test_timed_out: 'signInApps.testFailure.timedOut',
  sign_in_test_unknown: 'signInApps.testFailure.unknown',
}

export function signInTestFailureKey(code: string | null | undefined): string {
  return (code && SIGN_IN_TEST_FAILURE_KEYS[code.trim()]) || SIGN_IN_TEST_FAILURE_KEYS.sign_in_test_unknown
}
